import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { formatDateLong, formatRp, waLink, renderTemplate, DEFAULT_TEMPLATES } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tray, WhatsappLogo, CheckCircle, XCircle, Clock, ShieldCheck, Eye, Wallet, CaretDown } from "@phosphor-icons/react";
import { PaymentBadge } from "@/components/Badges";
import PaymentReviewDialog from "@/components/PaymentReviewDialog";
import DetailDialog from "@/components/DetailDialog";
import RoomChatButton from "@/components/RoomChatButton";
import { useHomecareRooms } from "@/hooks/useHomecareRooms";
import { OrderDetailDialog } from "@/pages/Orders";

const inputCls = "rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100";

const PENDING = ["Baru", "Menunggu Konfirmasi", "Menunggu Pembayaran"];
const CANCELLED = ["Batal", "No Show", "Dibatalkan Otomatis"];
const catOf = (r) => (PENDING.includes(r.status) ? "pending" : CANCELLED.includes(r.status) ? "cancelled" : "confirmed");
const witaToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Makassar", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const hhmm = (iso) => (iso ? String(iso).slice(11, 16) : "");

function StatusBadge({ r }) {
  if (r.is_order) {
    if (r.is_estimate)
      return <span data-testid={`status-badge-${r.id}`} className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700">Menunggu Konfirmasi Tagihan</span>;
    return <span data-testid={`status-badge-${r.id}`} className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700">Tagihan Dikonfirmasi</span>;
  }
  const cat = catOf(r);
  if (cat === "pending")
    return <span data-testid={`status-badge-${r.id}`} className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700">Menunggu Konfirmasi</span>;
  if (cat === "confirmed")
    return <span data-testid={`status-badge-${r.id}`} className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700">Sudah Dikonfirmasi</span>;
  return <span data-testid={`status-badge-${r.id}`} className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-bold text-rose-600">{r.status === "No Show" ? "Tidak Hadir" : r.status === "Dibatalkan Otomatis" ? "Dibatalkan Otomatis" : "Dibatalkan"}</span>;
}

export default function BookingMasuk() {
  const navigate = useNavigate();
  const { byReservation } = useHomecareRooms();
  // Aggregate unread per order, deduped by room_id (grouped children share one room).
  const orderUnread = (r) => {
    const seen = new Set();
    let n = 0;
    (r.children || []).forEach((c) => {
      const ri = byReservation[String(c.id)];
      if (ri && ri.room_id && !seen.has(ri.room_id)) { seen.add(ri.room_id); n += ri.unread || 0; }
    });
    return n;
  };
  const [items, setItems] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [reject, setReject] = useState(null);
  const [review, setReview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [billOrder, setBillOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [filter, setFilter] = useState("all");
  const [openBk, setOpenBk] = useState(null);

  const fillMsg = (key, r) =>
    renderTemplate((settings && settings[key]) || DEFAULT_TEMPLATES[key], {
      nama: r.nama_lengkap, layanan: r.jenis_layanan, kategori: r.kategori_layanan || "",
      tanggal: formatDateLong(r.tanggal_kunjungan), jam: r.jam_kunjungan,
      lokasi: r.lokasi || "", petugas: r.petugas_nama || "",
    });

  const load = () => {
    setLoading(true);
    api.get("/reservations", { params: { inbox: true, inbox_day: witaToday() } })
      .then((r) => setItems(r.data)).catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/staff").then((r) => setStaffList(r.data)).catch(() => {});
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);

  const assign = async (id, petugas_nama) => {
    try { await api.patch(`/reservations/${id}/assign`, { petugas_nama }); setItems((p) => p.map((x) => x.id === id ? { ...x, petugas_nama } : x)); toast.success("Petugas ditugaskan"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const accept = async (r) => {
    if (!r.petugas_nama) return toast.error("Pilih petugas dulu sebelum menerima");
    try {
      await api.patch(`/reservations/${r.id}/status`, { status: "Dikonfirmasi" });
      toast.success("Reservasi dikonfirmasi — membuka WhatsApp konfirmasi ke pasien");
      const url = waLink(r.no_whatsapp, fillMsg("tpl_approved", r));
      setTimeout(() => window.open(url, "_blank"), 300);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openBillOrder = async (orderId) => {
    try { const { data } = await api.get(`/orders/${orderId}`); setBillOrder(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doReject = async () => {
    const r = reject;
    try {
      if (r.is_order) {
        await Promise.all(r.children.map((c) => api.patch(`/reservations/${c.id}/status`, { status: "Batal" })));
      } else {
        await api.patch(`/reservations/${r.id}/status`, { status: "Batal" });
      }
      toast.success("Reservasi ditolak — membuka WhatsApp info ke pasien");
      const url = waLink(r.no_whatsapp, fillMsg("tpl_rejected", r));
      setTimeout(() => window.open(url, "_blank"), 300);
      setReject(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const isPaid = (r) => r.payment_mode && r.payment_mode !== "none";
  const total = items.length;
  const pendingCount = items.filter((r) => catOf(r) === "pending").length;
  const processedCount = total - pendingCount;
  const shown = items.filter((r) => filter === "all" ? true : filter === "pending" ? catOf(r) === "pending" : catOf(r) !== "pending");

  const FilterBtn = ({ id, label, count }) => (
    <button data-testid={`filter-${id}`} onClick={() => setFilter(id)}
      className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${filter === id ? "bg-[#7C4DE0] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
      {label}{typeof count === "number" ? ` (${count})` : ""}
    </button>
  );

  return (
    <div className="animate-fade-up space-y-5">
      <div>
        <div className="flex items-center gap-2"><Tray size={26} weight="duotone" className="text-[#7C4DE0]" /><h1 className="font-heading text-3xl font-extrabold text-slate-800">Booking Masuk</h1></div>
        <p data-testid="booking-count" className="mt-1 text-sm text-slate-500">
          <b className="text-slate-700">{total}</b> booking online masuk hari ini
        </p>
        <p className="text-xs text-slate-400">{pendingCount} menunggu konfirmasi • {processedCount} sudah diproses</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <FilterBtn id="all" label="Semua Hari Ini" count={total} />
        <FilterBtn id="pending" label="Menunggu" count={pendingCount} />
        <FilterBtn id="processed" label="Sudah Diproses" count={processedCount} />
      </div>

      {loading ? <p className="text-sm text-slate-400">Memuat...</p> : shown.length === 0 ? (
        <div data-testid="booking-masuk-empty" className="rounded-2xl border border-dashed border-rose-200 bg-white p-10 text-center text-slate-400">
          <CheckCircle size={40} weight="duotone" className="mx-auto text-emerald-400" />
          <p className="mt-2 text-sm">{total === 0 ? "Belum ada booking online masuk hari ini." : "Tidak ada booking pada filter ini."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {shown.map((r) => {
            const cat = catOf(r);
            return (
            <div key={r.id} data-testid={`booking-${r.id}`} className={`rounded-2xl border bg-white p-4 shadow-sm ${cat === "cancelled" ? "border-rose-100 opacity-90" : "border-rose-100/70"}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-heading font-bold text-slate-800">{r.nama_lengkap}{r.is_order ? <span className="ml-1 rounded bg-[#7C4DE0] px-1.5 py-0.5 text-[10px] font-bold text-white">Multi · {r.children_count}</span> : null}{r.is_order && (r.children || []).reduce((s, c) => s + ((byReservation[String(c.id)]?.unread) || 0), 0) > 0 ? <span data-testid={`bk-order-unread-${r.order_id}`} className="ml-1 inline-flex items-center gap-1 rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">🔔 {(r.children || []).reduce((s, c) => s + ((byReservation[String(c.id)]?.unread) || 0), 0)}</span> : null}</p>
                  <p className="text-xs text-slate-500">{r.no_whatsapp}</p>
                  <p className="text-[11px] text-slate-400">Masuk {hhmm(r.created_at)} WITA{r.confirmed_at ? ` · Dikonfirmasi ${hhmm(r.confirmed_at)}` : ""}{cat === "cancelled" && r.cancel_reason ? ` · ${r.cancel_reason}${r.cancelled_at ? " " + hhmm(r.cancelled_at) : ""}` : ""}</p>
                </div>
                <div className="flex flex-col items-end gap-1"><StatusBadge r={r} /><PaymentBadge status={r.payment_status} /></div>
              </div>
              <div className="mt-2 space-y-1 text-sm text-slate-600">
                <p>🌷 {r.jenis_layanan} {r.kategori_layanan ? <span className="text-xs text-slate-400">· {r.kategori_layanan}</span> : null}</p>
                <p className="flex items-center gap-1"><Clock size={14} /> {formatDateLong(r.tanggal_kunjungan)} · {r.jam_kunjungan}</p>
                <p>📍 {r.lokasi || "-"}</p>
              </div>

              {r.is_order && (
                <div className="mt-2">
                  <button data-testid={`bk-expand-${r.order_id}`} onClick={() => setOpenBk(openBk === r.order_id ? null : r.order_id)} className="inline-flex items-center gap-1 text-xs font-bold text-[#6A38C7]"><CaretDown size={14} className={`transition-transform ${openBk === r.order_id ? "rotate-180" : ""}`} /> Lihat {r.children_count} layanan</button>
                  {openBk === r.order_id && (
                    <ul className="mt-1 space-y-1">
                      {r.children.map((c) => (
                        <li key={c.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600">
                          <span className="min-w-0 truncate">{c.jenis_layanan} · {c.jam_kunjungan || "-"} · {c.petugas_nama || "Tanpa petugas"}</span>
                          <span className="shrink-0"><RoomChatButton reservation={c} roomInfo={byReservation[String(c.id)]} /></span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {isPaid(r) && (
                <div className="mt-2 grid grid-cols-3 gap-2 rounded-xl bg-[#FAF7FF] p-2.5 text-center text-xs">
                  <div><p className="text-slate-400">Tagihan</p><p className="font-bold text-slate-700">{formatRp(r.total_tagihan)}</p></div>
                  <div><p className="text-slate-400">Dibayar</p><p className="font-bold text-emerald-600">{formatRp(r.total_dibayar)}</p></div>
                  <div><p className="text-slate-400">Sisa</p><p className="font-bold text-rose-600">{formatRp(r.sisa_tagihan)}</p></div>
                </div>
              )}

              {cat === "pending" && r.payment_status === "Menunggu Verifikasi" && (
                <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3">
                  <p data-testid={`await-verify-${r.id}`} className="text-xs font-extrabold uppercase tracking-wide text-amber-700">⚠ Menunggu Verifikasi Pembayaran</p>
                  <button data-testid={`review-payment-${r.id}`} onClick={() => setReview(r)} className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-600"><ShieldCheck size={14} weight="bold" /> Lihat &amp; Verifikasi Pembayaran</button>
                </div>
              )}

              {cat === "pending" && !r.is_order && (
                <div className="mt-3">
                  <label className="mb-1 block text-xs font-semibold text-slate-500">Tugaskan Petugas</label>
                  <select data-testid={`assign-${r.id}`} value={r.petugas_nama || ""} onChange={(e) => assign(r.id, e.target.value)} className={inputCls + " w-full"}>
                    <option value="">-- Pilih Petugas --</option>
                    {staffList.map((s) => <option key={s.id} value={s.nama}>{s.nama} ({s.jenis})</option>)}
                  </select>
                </div>
              )}
              {cat !== "pending" && r.petugas_nama && (
                <p className="mt-3 text-xs text-slate-500">Petugas: <b className="text-slate-700">{r.petugas_nama}</b></p>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                <button data-testid={`detail-${r.id}`} onClick={() => setDetail(r)} className="inline-flex items-center gap-1 rounded-lg bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100"><Eye size={14} weight="bold" /> Detail</button>
                {!r.is_order && <RoomChatButton reservation={r} roomInfo={byReservation[String(r.id)]} />}
                {cat === "pending" && (
                  <>
                    {r.is_order ? (
                      <button data-testid={`bill-order-${r.order_id}`} onClick={() => openBillOrder(r.order_id)} className="inline-flex items-center gap-1 rounded-lg bg-[#7C4DE0] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#6A38C7]"><Wallet size={14} weight="bold" /> Atur &amp; Konfirmasi Tagihan</button>
                    ) : (
                      <>
                        <a data-testid={`wa-${r.id}`} href={waLink(r.no_whatsapp, fillMsg("tpl_approved", r))} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700"><WhatsappLogo size={14} weight="fill" /> WA ke Pasien</a>
                        <button data-testid={`accept-${r.id}`} onClick={() => accept(r)} className="inline-flex items-center gap-1 rounded-lg bg-[#7C4DE0] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#6A38C7]"><CheckCircle size={14} weight="bold" /> Terima &amp; Konfirmasi</button>
                      </>
                    )}
                    <button data-testid={`reject-${r.id}`} onClick={() => setReject(r)} className="inline-flex items-center gap-1 rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600"><XCircle size={14} /> Tolak</button>
                  </>
                )}
                {cat === "confirmed" && isPaid(r) && (
                  <button data-testid={`to-payment-${r.id}`} onClick={() => navigate("/pembayaran")} className="inline-flex items-center gap-1 rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"><Wallet size={14} weight="bold" /> Ke Pembayaran</button>
                )}
              </div>
            </div>
          );})}
        </div>
      )}

      <PaymentReviewDialog reservation={review} open={!!review} onClose={() => setReview(null)} onChange={load} />
      <DetailDialog reservation={detail} open={!!detail} onClose={() => setDetail(null)} onChanged={load} />
      {billOrder && <OrderDetailDialog order={billOrder} onClose={() => setBillOrder(null)} onChange={(o) => { if (o) setBillOrder(o); else setBillOrder(null); load(); }} />}

      <Dialog open={!!reject} onOpenChange={(v) => !v && setReject(null)}>
        <DialogContent className="rounded-2xl sm:max-w-sm" data-testid="reject-dialog">
          <DialogHeader><DialogTitle className="font-heading">Tolak Reservasi?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Reservasi <b>{reject?.nama_lengkap}</b> akan ditandai <b>Batal</b>, lalu WhatsApp info penolakan ke pasien akan dibuka.</p>
          <DialogFooter>
            <button onClick={() => setReject(null)} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
            <button data-testid="confirm-reject" onClick={doReject} className="rounded-xl bg-rose-500 px-5 py-2 text-sm font-bold text-white hover:bg-rose-600">Tolak &amp; Info Pasien</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
